<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { beaconAuditBatchApi } from '@/services/beaconAuditBatchApi';
import type { BeaconRateLimitTrip } from '@/types/beaconAuditBatch';

const trip = ref<BeaconRateLimitTrip | null>(null);
const loading = ref(false);
const showClearForm = ref(false);
const clearNote = ref('');

const visible = computed(() => trip.value !== null);

const trippedAgoText = computed(() => {
  if (!trip.value) return '';
  const trippedAt = new Date(trip.value.trippedAt);
  const now = new Date();
  const diffMs = now.getTime() - trippedAt.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
});

async function refresh() {
  loading.value = true;
  try {
    const status = await beaconAuditBatchApi.getRateLimitStatus();
    trip.value = status.tripped && status.trip ? status.trip : null;
  } catch (e) {
    // Fail closed: don't show a banner if the status endpoint is unreachable.
    trip.value = null;
    console.error('Failed to fetch beacon rate-limit status', e);
  } finally {
    loading.value = false;
  }
}

async function clearTrip() {
  loading.value = true;
  try {
    await beaconAuditBatchApi.clearRateLimit(clearNote.value.trim() || null);
    trip.value = null;
    showClearForm.value = false;
    clearNote.value = '';
  } catch (e: unknown) {
    const err = e as { response?: { status?: number } };
    if (err.response?.status === 404) {
      // Already cleared by another super admin.
      trip.value = null;
      showClearForm.value = false;
      alert('Rate-limit trip was already cleared by another super admin.');
    } else {
      alert('Failed to clear rate-limit trip.');
    }
  } finally {
    loading.value = false;
  }
}

onMounted(refresh);
</script>

<template>
  <bb-alert v-if="visible && trip" variant="error" class="rate-limit-alert">
    <strong class="bb-alert__title">Beacon rate limit tripped</strong>
    <p>
      Tripped {{ trippedAgoText }} by <code>{{ trip.trippedBy }}</code>. All Beacon API traffic is halted until
      cleared.
    </p>
    <div class="bb-alert-actions">
      <button
        type="button"
        class="bb-button -secondary -size-sm"
        :disabled="loading"
        aria-label="Refresh status"
        title="Refresh status"
        @click="refresh"
      >
        <bb-icon name="refresh" decorative></bb-icon>
      </button>
      <button v-if="!showClearForm" type="button" class="bb-button -destructive -size-sm" @click="showClearForm = true">
        Clear
      </button>
    </div>
    <!-- Static wrapper: bb-alert only distributes its children once, so v-if content must live inside one. -->
    <div class="clear-form-region">
      <div v-if="showClearForm" class="clear-form">
        <input v-model="clearNote" type="text" maxlength="500" placeholder="Optional note (why are you clearing this?)" />
        <button type="button" class="bb-button -secondary -size-sm" @click="showClearForm = false">Cancel</button>
        <button type="button" class="bb-button -destructive -size-sm" :disabled="loading" @click="clearTrip">
          Confirm clear
        </button>
      </div>
    </div>
  </bb-alert>
</template>

<style scoped>
.rate-limit-alert code {
  font-size: inherit;
}

.clear-form {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
}

.clear-form input {
  flex: 1;
  min-width: 200px;
}
</style>
